import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  FileText,
  TrendingUp,
  Lightbulb,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../lib/api';
import Button from '../components/Button';
import BudgetDocuments from '../components/budget/BudgetDocuments';
import StatCard, { StatCardRow } from '../components/StatCard';
import { DepartmentOverview, DepartmentInitiative } from '../types/budget';
import { usePageMetadata } from '../contexts/PageMetadataContext';

const FUND_COLORS: Record<string, string> = {
  GF: '#6366f1',
  CF: '#14b8a6',
  RF: '#f97316',
  FF: '#8b5cf6',
};

const FUND_LABELS: Record<string, string> = {
  GF: 'General Fund',
  CF: 'Cash Funds',
  RF: 'Reappropriated',
  FF: 'Federal Funds',
};

function formatCurrency(amount: number): string {
  if (Math.abs(amount) >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toFixed(0)}`;
}

const BudgetDepartmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageMetadata } = usePageMetadata();

  const [overview, setOverview] = useState<DepartmentOverview | null>(null);
  const [initiatives, setInitiatives] = useState<DepartmentInitiative[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [overviewRes, initRes] = await Promise.all([
          api.get(`/budget/departments/${id}/overview`),
          api.get(`/budget/departments/${id}/initiatives`),
        ]);
        if (overviewRes.data?.success) setOverview(overviewRes.data.data);
        if (initRes.data?.success) setInitiatives(initRes.data.data);
      } catch {
        // handled by empty state
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (overview) {
      setPageMetadata({
        title: overview.department.dept_name,
        description: `JBC documents and budget overview for ${overview.department.dept_name}`,
        backUrl: '/budget',
      });
    }
    return () => setPageMetadata(null);
  }, [overview, setPageMetadata]);

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="p-6">
        <Button onClick={() => navigate('/budget')} variant="secondary" size="sm" icon={ArrowLeft} className="mb-4">
          Back to budget
        </Button>
        <p className="text-text-tertiary dark:text-text-dark-tertiary">Department not found.</p>
      </div>
    );
  }

  const { department, fundingSummary, itemCount, billCount } = overview;

  const pieData = fundingSummary
    .filter(f => ['GF', 'CF', 'RF', 'FF'].includes(f.fund_type) && parseFloat(String(f.total_amount)) !== 0)
    .map(f => ({
      name: FUND_LABELS[f.fund_type] || f.fund_type,
      value: Math.abs(parseFloat(String(f.total_amount))),
      fund_type: f.fund_type,
    }));

  const totalRow = fundingSummary.find(f => f.fund_type === 'Total');
  const totalAmount = totalRow ? parseFloat(String(totalRow.total_amount)) : 0;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
            {department.dept_name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2.5 py-1 text-xs font-semibold rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              {department.dept_code}
            </span>
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              FY {overview.fiscalYear}
            </span>
          </div>
        </div>
      </div>

      <StatCardRow>
        <StatCard value={formatCurrency(totalAmount)} label="Total Request" variant="success" icon={TrendingUp} />
        <StatCard value={itemCount} label="Budget Items" icon={FileText} />
        <StatCard value={billCount} label="Linked Bills" variant="warning" icon={Lightbulb} />
      </StatCardRow>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {pieData.length > 0 && (
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-4">
              Funding Breakdown
            </h2>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  isAnimationActive={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.fund_type} fill={FUND_COLORS[entry.fund_type] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), '']}
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary, #fff)',
                    border: '1px solid var(--border-light, #e5e7eb)',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {initiatives.length > 0 && (
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-4 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              Key Policy Initiatives
            </h2>
            <ul className="space-y-3">
              {initiatives.map((init) => (
                <li
                  key={init.id}
                  className="text-sm text-text-secondary dark:text-text-dark-secondary pl-4 relative before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-primary-400"
                >
                  {init.initiative_text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
          JBC Documents
        </h2>
        <BudgetDocuments deptId={parseInt(id!)} />
      </div>
    </div>
  );
};

export default BudgetDepartmentDetailPage;
